/**
 * Company workspace — Team Report (08-company-workspace/02).
 *
 * Hiring managers see aggregated attempt metrics by Track, Level, and Test.
 * Shows average scores, median scores, and attempt counts.
 */

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Shell } from '../../components/shell/Shell.tsx';
import { Card } from '@/components/ui';
import { apiFetch } from '@/lib/api-client';

interface TeamReport {
  company_id: string;
  generated_at: string;
  by_track: Array<{
    track: string;
    avg_score: number;
    median_score: number;
    attempt_count: number;
  }>;
  by_level: Array<{
    level: string;
    avg_score: number;
    median_score: number;
    attempt_count: number;
  }>;
  by_test: Array<{
    test_id: string;
    title: string;
    avg_score: number;
    pass_rate: number;
    attempt_count: number;
  }>;
}

export function TeamReportPage(): JSX.Element {
  const params = useParams<{ id: string }>();
  const companyId = params.id ?? '';

  const [report, setReport] = useState<TeamReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);

    apiFetch(`/api/companies/${companyId}/team-report`, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`Failed to load team report: ${res.statusText}`);
        }
        const data = (await res.json()) as { ok: boolean; data: TeamReport };
        if (!data.ok) {
          throw new Error('API returned error');
        }
        setReport(data.data);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Unknown error');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [companyId]);

  const totalAttempts = report
    ? report.by_track.reduce((sum: number, t) => sum + t.attempt_count, 0)
    : 0;

  const completedAttempts = report
    ? report.by_test.filter((t: typeof report.by_test[0]) => t.attempt_count > 0).length
    : 0;

  const overallAvgScore = report
    ? (report.by_track.reduce((sum: number, t) => sum + t.avg_score * t.attempt_count, 0) / totalAttempts)
    : 0;

  return (
    <Shell mode="company" densityMode="workspace" companyId={companyId}>
      <header className="mb-8">
        <p className="font-mono text-mono text-muted-2">
          COMPANY · TEAM REPORT
        </p>
        <h1 className="mt-2 font-sans text-headline font-medium tracking-tight">
          Team Report
        </h1>
        <p className="mt-2 max-w-prose font-sans text-body text-muted">
          Aggregated attempt analytics across all tracks, levels, and tests.
        </p>
      </header>

      {error && (
          <div className="mb-4 rounded border border-error/20 bg-error/10 p-4">
            <p className="font-sans text-body text-error">{error}</p>
          </div>
        )}

        {isLoading ? (
          <p className="font-sans text-body text-muted">Loading report...</p>
        ) : report ? (
          <>
            {/* Summary metrics */}
            <section className="mb-12">
              <h2 className="mb-4 font-sans text-title font-medium">Overall Metrics</h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Card variant="default">
                  <p className="font-mono text-xs text-muted-2 uppercase">Total Attempts</p>
                  <p className="mt-2 font-mono text-h2 font-semibold leading-none text-ink">
                    {totalAttempts}
                  </p>
                </Card>
                <Card variant="default">
                  <p className="font-mono text-xs text-muted-2 uppercase">Average Score</p>
                  <p className="mt-2 font-mono text-h2 font-semibold leading-none text-ink">
                    {isNaN(overallAvgScore) ? '—' : overallAvgScore.toFixed(1)}%
                  </p>
                </Card>
                <Card variant="default">
                  <p className="font-mono text-xs text-muted-2 uppercase">Tests Used</p>
                  <p className="mt-2 font-mono text-h2 font-semibold leading-none text-ink">
                    {completedAttempts}
                  </p>
                </Card>
              </div>
            </section>

            {/* By Track */}
            <section className="mb-12">
              <h2 className="mb-4 font-sans text-title font-medium">By Track</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-muted/20">
                      <th className="px-4 py-3 text-left font-sans text-label font-medium text-ink">
                        Track
                      </th>
                      <th className="px-4 py-3 text-right font-sans text-label font-medium text-ink">
                        Attempts
                      </th>
                      <th className="px-4 py-3 text-right font-sans text-label font-medium text-ink">
                        Avg Score
                      </th>
                      <th className="px-4 py-3 text-right font-sans text-label font-medium text-ink">
                        Median Score
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.by_track.map((row: typeof report.by_track[0]) => (
                      <tr key={row.track} className="border-b border-muted/10 hover:bg-card/50">
                        <td className="px-4 py-3 font-sans text-body text-ink">
                          {row.track}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-body text-muted">
                          {row.attempt_count}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-body text-ink">
                          {row.avg_score.toFixed(1)}%
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-body text-ink">
                          {row.median_score.toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* By Level */}
            <section className="mb-12">
              <h2 className="mb-4 font-sans text-title font-medium">By Level</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-muted/20">
                      <th className="px-4 py-3 text-left font-sans text-label font-medium text-ink">
                        Level
                      </th>
                      <th className="px-4 py-3 text-right font-sans text-label font-medium text-ink">
                        Attempts
                      </th>
                      <th className="px-4 py-3 text-right font-sans text-label font-medium text-ink">
                        Avg Score
                      </th>
                      <th className="px-4 py-3 text-right font-sans text-label font-medium text-ink">
                        Median Score
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.by_level.map((row: typeof report.by_level[0]) => (
                      <tr key={row.level} className="border-b border-muted/10 hover:bg-card/50">
                        <td className="px-4 py-3 font-sans text-body text-ink">
                          {row.level}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-body text-muted">
                          {row.attempt_count}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-body text-ink">
                          {row.avg_score.toFixed(1)}%
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-body text-ink">
                          {row.median_score.toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* By Test */}
            <section>
              <h2 className="mb-4 font-sans text-title font-medium">By Test</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-muted/20">
                      <th className="px-4 py-3 text-left font-sans text-label font-medium text-ink">
                        Test
                      </th>
                      <th className="px-4 py-3 text-right font-sans text-label font-medium text-ink">
                        Attempts
                      </th>
                      <th className="px-4 py-3 text-right font-sans text-label font-medium text-ink">
                        Avg Score
                      </th>
                      <th className="px-4 py-3 text-right font-sans text-label font-medium text-ink">
                        Pass Rate
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.by_test.map((row: typeof report.by_test[0]) => (
                      <tr key={row.test_id} className="border-b border-muted/10 hover:bg-card/50">
                        <td className="px-4 py-3 font-sans text-body text-ink">
                          {row.title}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-body text-muted">
                          {row.attempt_count}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-body text-ink">
                          {row.avg_score.toFixed(1)}%
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-body text-ink">
                          {(row.pass_rate * 100).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        ) : (
        <p className="font-sans text-body text-muted">No report data available.</p>
      )}
    </Shell>
  );
}
